/**
 * HeroCard - Hero section with headline, subtitle, media, and CTA.
 *
 * Accepts two prop naming conventions so it works across YAML schemas:
 *   - Standard:   headline / subheadline / imageSrc / imageAlt / backgroundImage
 *   - Shorthand:  title / subtitle / image / image_alt / overlay / height / text_color
 *
 * Variants: 'Split' (default) | 'Focused' | 'Minimal' | 'ImageOverlay'
 */
import React from 'react';
import { assetUrl } from '../../lib/assets';

export interface HeroCardProps {
  // Standard naming
  variant?: 'Split' | 'Focused' | 'Minimal' | 'ImageOverlay';
  eyebrow?: string;
  headline?: string;
  subheadline?: string;
  imageSrc?: string;
  imageAlt?: string;
  backgroundImage?: string;
  badge?: string;
  primaryCta?: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
  // YAML alt naming
  cta?: { label: string; href: string };
  // Shorthand naming (alt convention)
  title?: string;
  subtitle?: string;
  image?: string;
  image_alt?: string;
  height?: number;
  overlay?: boolean;
  text_color?: string;
  className?: string;
  style?: React.CSSProperties;
}

const HeroCard: React.FC<HeroCardProps> = (props) => {
  const {
    variant = 'Split',
    eyebrow,
    badge,
    className = '',
  } = props;

  // Resolve prop aliases
  const primaryCta = props.primaryCta || props.cta;
  const secondaryCta = props.secondaryCta;
  const heading = props.headline || props.title || '';
  const sub = props.subheadline || props.subtitle || '';
  const imgSrc = assetUrl(props.imageSrc || props.image || '');
  const imgAlt = props.imageAlt || props.image_alt || '';
  const heroHeight = props.height || (variant === 'ImageOverlay' ? 480 : undefined);
  const textColor = props.text_color || '#ffffff';

  // ── ImageOverlay (full-bleed photo with dark gradient + centered text) ──
  if (variant === 'ImageOverlay' || (imgSrc && props.overlay)) {
    return (
      <section
        className={`hero-card hero-image-overlay ${className}`}
        style={{
          position: 'relative',
          width: '100%',
          minHeight: heroHeight ? `${heroHeight}px` : '420px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
        aria-label="Hero section"
      >
        {imgSrc && (
          <img
            src={imgSrc}
            alt={imgAlt}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center', zIndex: 0,
            }}
            loading="eager"
          />
        )}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.6) 100%)',
        }} aria-hidden="true" />
        <div className="layout-container" style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: 'var(--spacing-16) var(--spacing-6)' }}>
          {eyebrow && (
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 'var(--spacing-3)' }}>
              {eyebrow}
            </p>
          )}
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 'var(--font-weight-extrabold)', lineHeight: 'var(--line-height-tight)', color: textColor, marginBottom: 'var(--spacing-5)', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
            {heading}
          </h1>
          {sub && (
            <p style={{ fontSize: 'var(--font-size-lg)', color: textColor, opacity: 0.92, maxWidth: '680px', margin: '0 auto var(--spacing-8)', lineHeight: 'var(--line-height-relaxed)', textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
              {sub}
            </p>
          )}
          <div style={{ display: 'flex', gap: 'var(--spacing-4)', justifyContent: 'center', flexWrap: 'wrap' }}>
            {primaryCta && (
              <a href={primaryCta.href} className="btn btn-lg" style={{ backgroundColor: '#ffffff', color: 'var(--color-primary)' }}>
                {primaryCta.label}
              </a>
            )}
            {secondaryCta && (
              <a href={secondaryCta.href} className="btn btn-lg" style={{ backgroundColor: 'transparent', color: '#ffffff', borderColor: 'rgba(255,255,255,0.6)' }}>
                {secondaryCta.label}
              </a>
            )}
          </div>
        </div>
      </section>
    );
  }

  // ── Focused variant (gradient background, centered) ──
  if (variant === 'Focused') {
    return (
      <section
        className={`hero-card hero-focused layout-full ${className}`}
        style={{
          background: props.backgroundImage
            ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${props.backgroundImage}) center/cover no-repeat`
            : 'var(--color-primary)',
          color: '#ffffff',
          minHeight: heroHeight ? `${heroHeight}px` : '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...props.style,
        }}
        aria-label="Hero section"
      >
        <div className="layout-container" style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', padding: 'var(--spacing-16) var(--spacing-6)' }}>
          {badge && <span className="badge badge-accent" style={{ marginBottom: 'var(--spacing-4)', display: 'inline-block' }}>{badge}</span>}
          {eyebrow && <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 'var(--spacing-3)' }}>{eyebrow}</p>}
          <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 'var(--font-weight-extrabold)', lineHeight: 'var(--line-height-tight)', marginBottom: 'var(--spacing-6)', color: '#ffffff' }}>{heading}</h1>
          {sub && <p style={{ fontSize: 'var(--font-size-xl)', color: 'rgba(255,255,255,0.85)', marginBottom: 'var(--spacing-8)', maxWidth: '600px', margin: '0 auto var(--spacing-8)' }}>{sub}</p>}
          <div style={{ display: 'flex', gap: 'var(--spacing-4)', justifyContent: 'center', flexWrap: 'wrap' }}>
            {primaryCta && <a href={primaryCta.href} className="btn btn-lg" style={{ backgroundColor: '#ffffff', color: 'var(--color-primary)', borderColor: '#ffffff' }}>{primaryCta.label}</a>}
            {secondaryCta && <a href={secondaryCta.href} className="btn btn-lg" style={{ backgroundColor: 'transparent', color: '#ffffff', borderColor: 'rgba(255,255,255,0.5)' }}>{secondaryCta.label}</a>}
          </div>
        </div>
      </section>
    );
  }

  // ── Split variant (default: text left, image right) ──
  return (
    <section
      className={`hero-card hero-split layout-full ${className}`}
      style={{ backgroundColor: 'var(--color-background)' }}
      aria-label="Hero section"
    >
      <div
        className="layout-container"
        style={{
          display: 'grid',
          gridTemplateColumns: imgSrc ? '1fr 1fr' : '1fr',
          gap: 'var(--spacing-12)',
          alignItems: 'center',
          minHeight: heroHeight ? `${heroHeight}px` : undefined,
          padding: 'var(--spacing-16) var(--spacing-6)',
        }}
      >
        <div>
          {badge && <span className="badge badge-primary" style={{ marginBottom: 'var(--spacing-4)', display: 'inline-block' }}>{badge}</span>}
          {eyebrow && <p style={{ color: 'var(--color-primary)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 'var(--spacing-3)' }}>{eyebrow}</p>}
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 'var(--font-weight-extrabold)', lineHeight: 'var(--line-height-tight)', color: 'var(--color-text)', marginBottom: 'var(--spacing-6)' }}>{heading}</h1>
          {sub && <p style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-8)', lineHeight: 'var(--line-height-relaxed)' }}>{sub}</p>}
          <div style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
            {primaryCta && <a href={primaryCta.href} className="btn btn-primary btn-lg">{primaryCta.label}</a>}
            {secondaryCta && <a href={secondaryCta.href} className="btn btn-secondary btn-lg">{secondaryCta.label}</a>}
          </div>
        </div>
        {imgSrc && (
          <div style={{ borderRadius: 'var(--radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-xl)' }}>
            <img src={imgSrc} alt={imgAlt} style={{ width: '100%', height: 'auto', display: 'block' }} loading="eager" />
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroCard;
